import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

/**
 * WebSocket service for real-time ClickHouse deployment log streaming.
 * Uses SockJS + STOMP protocol. Falls back to REST polling.
 * Mirrors the pattern from syntheticsWebSocketService.js.
 */
class DeploymentWebSocketService {
  constructor() {
    this.stompClient = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.connectionListeners = [];
    this.subscriptions = new Map();

    // Polling fallback
    this.pollingEnabled = false;
    this.pollingInterval = null;
    this.pollingDelay = 5000;
    this.lastEventTimestamp = null;
  }

  getWebSocketUrl() {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  }

  connect() {
    if (this.connected || (this.stompClient && this.stompClient.active)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = this.getWebSocketUrl();

        this.stompClient = new Client({
          webSocketFactory: () => new SockJS(wsUrl),
          reconnectDelay: this.reconnectDelay,
          heartbeatIncoming: 10000,
          heartbeatOutgoing: 10000,
          debug: () => {},
          onConnect: () => {
            this.connected = true;
            this.reconnectAttempts = 0;
            this.pollingEnabled = false;
            this.stopPolling();
            this.listeners.forEach((_, topic) => this.subscribeToTopic(topic));
            this.notifyConnectionListeners(true);
            resolve();
          },
          onDisconnect: () => {
            this.connected = false;
            this.subscriptions.clear();
            this.notifyConnectionListeners(false);
          },
          onStompError: (frame) => {
            console.error('[DeployWS] STOMP error:', frame.headers['message']);
          },
          onWebSocketClose: (event) => {
            this.connected = false;
            this.subscriptions.clear();
            this.notifyConnectionListeners(false);
            if (event.code !== 1000) this.handleReconnect();
          }
        });

        this.stompClient.activate();
      } catch (error) {
        this.enablePollingFallback();
        reject(error);
      }
    });
  }

  disconnect() {
    this.stopPolling();
    if (this.stompClient) {
      try { this.stompClient.deactivate(); } catch (e) { /* ignore */ }
      this.stompClient = null;
      this.connected = false;
      this.subscriptions.clear();
      this.notifyConnectionListeners(false);
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.enablePollingFallback();
      return;
    }
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.min(this.reconnectAttempts, 3);
    setTimeout(() => this.connect().catch(() => {}), delay);
  }

  subscribeToTopic(topic) {
    if (!this.connected || !this.stompClient?.active || this.subscriptions.has(topic)) return;
    const sub = this.stompClient.subscribe(topic, (message) => {
      try {
        const data = JSON.parse(message.body);
        this.lastEventTimestamp = Date.now();
        this.notifyListeners(topic, data);
      } catch (e) {
        console.error('[DeployWS] Parse error:', e);
      }
    });
    this.subscriptions.set(topic, sub);
  }

  unsubscribeFromTopic(topic) {
    const sub = this.subscriptions.get(topic);
    if (sub) { sub.unsubscribe(); this.subscriptions.delete(topic); }
  }

  addListener(topic, callback) {
    if (!this.listeners.has(topic)) {
      this.listeners.set(topic, new Set());
      if (this.connected) this.subscribeToTopic(topic);
    }
    this.listeners.get(topic).add(callback);
    return () => this.removeListener(topic, callback);
  }

  removeListener(topic, callback) {
    const topicListeners = this.listeners.get(topic);
    if (topicListeners) {
      topicListeners.delete(callback);
      if (topicListeners.size === 0) {
        this.unsubscribeFromTopic(topic);
        this.listeners.delete(topic);
      }
    }
  }

  notifyListeners(topic, data) {
    const topicListeners = this.listeners.get(topic);
    if (topicListeners) {
      topicListeners.forEach(cb => { try { cb(data); } catch (e) { /* ignore */ } });
    }
  }

  addConnectionListener(callback) {
    this.connectionListeners.push(callback);
    callback(this.connected);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(cb => cb !== callback);
    };
  }

  notifyConnectionListeners(connected) {
    this.connectionListeners.forEach(cb => { try { cb(connected); } catch (e) { /* ignore */ } });
  }

  enablePollingFallback() {
    if (this.pollingEnabled) return;
    this.pollingEnabled = true;
    this.startPolling();
  }

  startPolling() {
    if (this.pollingInterval) return;
    this.pollingInterval = setInterval(() => {
      this.notifyConnectionListeners(false);
    }, this.pollingDelay);
  }

  stopPolling() {
    if (this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; }
  }

  // === Convenience methods for deployment topics ===

  /** Subscribe to ALL deployment events across all nodes */
  onDeploymentUpdate(callback) {
    return this.addListener('/topic/infrastructure/deployments', callback);
  }

  /** Subscribe to deployment events for a specific node */
  onNodeDeploymentUpdate(nodeId, callback) {
    return this.addListener(`/topic/infrastructure/deployments/${nodeId}`, callback);
  }

  isConnected() { return this.connected; }
  isPolling() { return this.pollingEnabled; }
}

const deploymentWebSocketService = new DeploymentWebSocketService();
export default deploymentWebSocketService;
